import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import TodaysPicksSection from '@/components/app-entry/TodaysPicksSection';
import WeeklySelectionCard from '@/components/app-entry/WeeklySelectionCard';
import CardSelectView from '@/components/reveal/CardSelectView';
import SectionHeader from '@/components/app-entry/SectionHeader';
import { dailyRevealKeys } from '@/hooks/useDailyReveal';
import type { CardDetails, DayFixture, RevealCard } from '@/types/reveal';

type Mode = 'entry' | 'cards';

type FixtureStatus = DayFixture['status'];

function makeFixture(status: FixtureStatus, teamExternalId: string): DayFixture {
  return {
    id: `debug-fixture-${status}-${teamExternalId}`,
    externalFixtureId: `debug-ext-${status}-${teamExternalId}`,
    homeTeam: 'New York Yankees',
    homeTeamId: teamExternalId,
    awayTeam: 'Boston Red Sox',
    awayTeamId: 'debug-team-bos',
    startTime: new Date(Date.now() - 60 * 60_000).toISOString(),
    status,
    ...(status === 'finished' ? { homeScore: 5, awayScore: 3 } : {}),
    ...(status === 'live' ? { homeScore: 2, awayScore: 1, inning: 'TOP 4TH' } : {}),
  };
}

function makeRevealCard(overrides: Partial<RevealCard> = {}): RevealCard {
  return {
    id: 'debug-card-id',
    cardId: 'debug-card-id',
    userCardId: 'debug-user-card-id',
    tier: 'silver',
    hit: '1+ HIT',
    firstName: 'JOHN',
    lastName: 'DOE',
    displayName: 'John Doe',
    sport: 'MLB',
    team: 'NYY',
    teamFull: 'New York Yankees',
    teamExternalId: 'debug-team-nyy',
    number: '44',
    position: '1B',
    battingAverage: '0.288',
    awayTeam: 'Boston Red Sox',
    homeTeam: 'New York Yankees',
    time: '',
    playerImage: '',
    isSelected: true,
    ...overrides,
  };
}

function makeCardDetails(): CardDetails {
  return {
    id: 'debug-card-details-id',
    externalId: 'debug-player-1',
    teamExternalId: 'debug-team-nyy',
    displayName: 'John Doe',
    firstName: 'John',
    lastName: 'Doe',
    position: '1B',
    teamName: 'New York Yankees',
    teamAbbreviation: 'NYY',
    jerseyNumber: '44',
    battingAverage: '0.288',
    statValue: '0.288',
    rating: 'silver',
    injuryStatus: 'healthy',
    isActive: true,
    photoUrl: null,
    sport: 'MLB',
    createdAt: new Date(0).toISOString(),
    updatedAt: new Date(0).toISOString(),
  };
}

// Today's Pick scenarios take the fixture directly as a prop.

const TODAYS_PICK_SCENARIOS: {
  key: string;
  label: string;
  note: string;
  fixtureStatus: FixtureStatus;
  entryStatus: 'picked' | 'graded';
  totalHits: number;
  ppdHits: number;
}[] = [
  {
    key: 'postponed-picked',
    label: 'Postponed — before grading',
    note: 'Pick on a postponed game, day still open. Edit stays available (postponed does not lock).',
    fixtureStatus: 'postponed',
    entryStatus: 'picked',
    totalHits: 0,
    ppdHits: 0,
  },
  {
    key: 'cancelled-picked',
    label: 'Canceled — before grading',
    note: 'Pick on a canceled game, day still open. Selection locks (no edit).',
    fixtureStatus: 'cancelled',
    entryStatus: 'picked',
    totalHits: 0,
    ppdHits: 0,
  },
  {
    key: 'postponed-graded',
    label: 'Postponed — graded with credit',
    note: 'Graded entry with ppdHits > 0 on a postponed game → "Postponement Credit".',
    fixtureStatus: 'postponed',
    entryStatus: 'graded',
    totalHits: 1,
    ppdHits: 1,
  },
  {
    key: 'cancelled-graded',
    label: 'Canceled — graded with credit',
    note: 'Graded entry with ppdHits > 0 on a canceled game → "Cancelation Credit".',
    fixtureStatus: 'cancelled',
    entryStatus: 'graded',
    totalHits: 1,
    ppdHits: 1,
  },
  {
    key: 'finished-won',
    label: 'Comparison: graded win (normal)',
    note: 'Normal graded win for visual comparison.',
    fixtureStatus: 'finished',
    entryStatus: 'graded',
    totalHits: 2,
    ppdHits: 0,
  },
  {
    key: 'finished-lost',
    label: 'Comparison: graded loss (normal)',
    note: 'Normal graded loss for visual comparison.',
    fixtureStatus: 'finished',
    entryStatus: 'graded',
    totalHits: 0,
    ppdHits: 0,
  },
];

// Weekly Selection scenarios read the fixture from the React Query cache, primed per fake dayId.

const WEEKLY_SCENARIOS: {
  key: string;
  label: string;
  note: string;
  fixtureStatus: FixtureStatus;
  isGraded: boolean;
  totalHits: number;
  ppdCredited: boolean;
}[] = [
  {
    key: 'weekly-postponed-pending',
    label: 'Postponed — pending',
    note: 'Not yet graded; game postponed.',
    fixtureStatus: 'postponed',
    isGraded: false,
    totalHits: 0,
    ppdCredited: false,
  },
  {
    key: 'weekly-cancelled-pending',
    label: 'Canceled — pending',
    note: 'Not yet graded; game canceled.',
    fixtureStatus: 'cancelled',
    isGraded: false,
    totalHits: 0,
    ppdCredited: false,
  },
  {
    key: 'weekly-postponed-credit',
    label: 'Postponed — graded with credit',
    note: 'Graded with ppd credit → "Postponement Credit".',
    fixtureStatus: 'postponed',
    isGraded: true,
    totalHits: 1,
    ppdCredited: true,
  },
  {
    key: 'weekly-cancelled-credit',
    label: 'Canceled — graded with credit',
    note: 'Graded with ppd credit → "Cancelation Credit".',
    fixtureStatus: 'cancelled',
    isGraded: true,
    totalHits: 1,
    ppdCredited: true,
  },
  {
    key: 'weekly-finished-won',
    label: 'Comparison: graded win (normal)',
    note: 'Normal graded win for visual comparison.',
    fixtureStatus: 'finished',
    isGraded: true,
    totalHits: 2,
    ppdCredited: false,
  },
  {
    key: 'weekly-finished-lost',
    label: 'Comparison: graded loss (normal)',
    note: 'Normal graded loss for visual comparison.',
    fixtureStatus: 'finished',
    isGraded: true,
    totalHits: 0,
    ppdCredited: false,
  },
];

const CARD_SELECT_CARDS: RevealCard[] = [
  makeRevealCard({
    id: 'debug-card-postponed',
    cardId: 'debug-card-postponed',
    userCardId: 'debug-uc-postponed',
    firstName: 'PAT',
    lastName: 'POSTPONED',
    displayName: 'Pat Postponed',
    teamExternalId: 'debug-team-postponed',
    isSelected: false,
  }),
  makeRevealCard({
    id: 'debug-card-cancelled',
    cardId: 'debug-card-cancelled',
    userCardId: 'debug-uc-cancelled',
    firstName: 'CARL',
    lastName: 'CANCELED',
    displayName: 'Carl Canceled',
    teamExternalId: 'debug-team-cancelled',
    isSelected: false,
  }),
  makeRevealCard({
    id: 'debug-card-scheduled',
    cardId: 'debug-card-scheduled',
    userCardId: 'debug-uc-scheduled',
    firstName: 'SAM',
    lastName: 'SCHEDULED',
    displayName: 'Sam Scheduled',
    teamExternalId: 'debug-team-scheduled',
    isSelected: false,
  }),
];

const CARD_SELECT_FIXTURES: DayFixture[] = [
  makeFixture('postponed', 'debug-team-postponed'),
  makeFixture('cancelled', 'debug-team-cancelled'),
  makeFixture('scheduled', 'debug-team-scheduled'),
];

function ModeLink({ mode, current, label }: { mode: Mode; current: Mode; label: string }) {
  const active = mode === current;
  return (
    <a
      href={`?mode=${mode}`}
      className={`rounded px-3 py-1.5 text-[12px] font-semibold uppercase transition-colors ${
        active ? 'bg-[#53d337] text-black' : 'bg-[#2a2a2a] text-white/80 hover:bg-[#3a3a3a]'
      }`}
    >
      {label}
    </a>
  );
}

function ScenarioNote({ label, note }: { label: string; note: string }) {
  return (
    <div className="mb-2 mt-6 rounded-[8px] border border-[#3a3a3a] bg-[#1a1a1a] px-3 py-2">
      <p className="text-[13px] font-semibold text-[#53d337]">{label}</p>
      <p className="mt-0.5 text-[11px] font-medium text-[#8a8a8a]">{note}</p>
    </div>
  );
}

export default function DebugPpdStatesView() {
  const queryClient = useQueryClient();

  const mode = useMemo<Mode>(() => {
    const raw = new URLSearchParams(window.location.search).get('mode');
    return raw === 'cards' ? 'cards' : 'entry';
  }, []);

  // WeeklySelectionCard fetches fixtures itself via useDayFixtures(dayId), so prime the
  // React Query cache with a mock fixture per fake dayId before the cards mount.
  useState(() => {
    for (const s of WEEKLY_SCENARIOS) {
      queryClient.setQueryData(dailyRevealKeys.fixtures(s.key), {
        fixtures: [makeFixture(s.fixtureStatus, 'debug-team-nyy')],
      });
    }
  });

  if (mode === 'cards') {
    return (
      <main className="font-druk min-h-screen bg-[#121212] text-white flex flex-col">
        <div className="w-full md:max-w-[500px] md:mx-auto flex-1 flex flex-col">
          <div className="flex items-center justify-center gap-2 px-4 pt-4">
            <ModeLink mode="entry" current={mode} label="Entry states" />
            <ModeLink mode="cards" current={mode} label="Card select" />
          </div>
          <p className="mt-2 px-4 text-center text-[11px] font-medium text-[#8a8a8a]">
            Carousel: Postponed (selectable), Canceled (locked), Scheduled (normal). Selecting stays
            on this page.
          </p>
          <CardSelectView
            cards={CARD_SELECT_CARDS}
            dayId="debug-ppd-day"
            fixtures={CARD_SELECT_FIXTURES}
            onCardPicked={async () => {
              throw new Error('debug: staying on picker');
            }}
          />
        </div>
      </main>
    );
  }

  return (
    <main className="font-druk min-h-screen bg-[#121212] text-white flex flex-col">
      <div className="w-full md:max-w-[500px] md:mx-auto md:shadow-2xl flex-1">
        <div className="w-full bg-[#121212] px-4 pb-12 pt-4">
          <SectionHeader title="Debug: Postponed / Canceled States" />
          <div className="mt-3 flex items-center gap-2">
            <ModeLink mode="entry" current={mode} label="Entry states" />
            <ModeLink mode="cards" current={mode} label="Card select" />
          </div>

          <h2 className="mt-8 text-[15px] font-bold uppercase tracking-wide text-white">
            Today&apos;s Pick
          </h2>
          {TODAYS_PICK_SCENARIOS.map(s => (
            <div key={s.key}>
              <ScenarioNote label={s.label} note={s.note} />
              <TodaysPicksSection
                pick={makeRevealCard()}
                totalHits={s.totalHits}
                ppdHits={s.ppdHits}
                fixture={makeFixture(s.fixtureStatus, 'debug-team-nyy')}
                entryStatus={s.entryStatus}
                isPickCutoffReached={false}
                onEdit={() => {}}
              />
            </div>
          ))}

          <h2 className="mt-10 text-[15px] font-bold uppercase tracking-wide text-white">
            Weekly Selections
          </h2>
          {WEEKLY_SCENARIOS.map(s => (
            <div key={s.key}>
              <ScenarioNote label={s.label} note={s.note} />
              <WeeklySelectionCard
                dayId={s.key}
                card={makeCardDetails()}
                dateLabel="Debug day"
                totalHits={s.totalHits}
                isGraded={s.isGraded}
                won={s.isGraded && s.totalHits > 0}
                ppdCredited={s.ppdCredited}
                dayStatus="graded"
              />
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
